import type { DispatchBooking, LatestContactBookings, SubjectBooking } from '../../../api/types';
import type { LoadState } from '../../../lib/useLoad';
import { clientRef, ourRef } from '../../../lib/reservationRef';

/**
 * What a contact's last reservation says, for the two places that print it in
 * full — the mail layout's strip under the subject (MailContact) and the
 * three-pane layout's strip under the header (ThreadReservation).
 *
 * Decided once, because the empties are where it goes wrong. A read that could
 * not run, a contact with no dispatch booker and a booker who has never
 * booked are three different sentences — the contact panel's BookingsCard rule
 * — and two strips each deciding them would come to disagree about which is
 * which. Each layout keeps its own shape; this is only what it says.
 */
export type LastReservationState =
  | { kind: 'loading' }
  /** Something stopped the read: say so, never a silent blank. */
  | { kind: 'warn'; text: string }
  /** The read ran and there is nothing to show — said in words. */
  | { kind: 'none'; text: string }
  | { kind: 'booking'; booking: DispatchBooking };

/**
 * Only what the decision reads, so a load typed `| null` — the three-pane
 * strip's, which asks nothing for an unlinked contact — passes as well as the
 * mail layout's: LoadState itself is invariant in its data.
 */
export type LatestRead = Pick<LoadState<LatestContactBookings>, 'data' | 'loading' | 'error'>;

export function lastReservationState(
  latest: LatestRead,
  /** What the conversation row says about the link, for the case the read is silent. */
  linked: boolean,
): LastReservationState {
  if (latest.loading && !latest.data) return { kind: 'loading' };
  // "No bookings" and "could not read bookings" need opposite responses from
  // the agent, so a failure is named rather than drawn as an empty strip.
  const unavailable = latest.error
    ? latest.error.message || 'Reservations could not be loaded.'
    : (latest.data?.unavailable ?? null);
  if (unavailable) return { kind: 'warn', text: unavailable };
  // Asked about one contact and not answered: there is no dispatch customer or
  // affiliate behind it — unless the row says there is, in which case the service left
  // it out (a contact this agent may not open) and "not linked" would be a
  // guess. Answered with null: there is, and it has never booked.
  const entry = latest.data?.payload[0];
  if (!entry && linked) return { kind: 'warn', text: 'Reservations couldn’t be checked for this contact.' };
  if (!entry) return { kind: 'none', text: 'Not linked to a dispatch customer or affiliate yet.' };
  if (!entry.booking) return { kind: 'none', text: 'No rides or quotes yet.' };
  return { kind: 'booking', booking: entry.booking };
}

/**
 * How a strip or a chip says the subject named this reservation: "Ref #11410
 * in the subject" for dispatch's own number, "Client Ref #PO-778 in the
 * subject" for the booker's — the two are different claims, and the agent
 * reading "#778" should know whose number it was.
 */
export function subjectNamed(named: Pick<SubjectBooking, 'matchedOn' | 'reference'>): string {
  return `${named.matchedOn === 'ours' ? ourRef('job', named.reference) : clientRef(named.reference)} in the subject`;
}
